/**
 * What Continue means for the setup step that owns an open revision.
 *
 * The navigation policy answers "may the user leave for a later destination".
 * This answers a different question: on the setup step itself, Continue is the
 * action that commits the revision, so it must stay usable exactly while there
 * is something to commit.
 */

import {
  isSetupRevisionSettled,
  nextSetupRevisionStatus,
  type SetupRevisionEvent,
  type SetupRevisionStatus,
} from './revisionState';

/**
 * The event that moves the revision into reconciling, or `null` when the status
 * has nothing to commit. Derived from the status machine rather than a second
 * hand-kept status list, so the two can never disagree.
 */
export const resolveSetupCommitEvent = (
  status: SetupRevisionStatus
): SetupRevisionEvent | null => {
  const candidates: SetupRevisionEvent[] = ['saveAndContinue', 'reconfirmUnchanged'];

  return candidates.find(
    (event) => nextSetupRevisionStatus(status, event) === 'reconciling'
  ) ?? null;
};

/**
 * Whether the revision itself forbids continuing from the setup step it owns:
 * true while reconciliation is in flight and while recovery is required.
 */
export const isSetupStepBlockedByRevision = (status: SetupRevisionStatus): boolean =>
  resolveSetupCommitEvent(status) === null && !isSetupRevisionSettled(status);
